import React from "react";
import { Order, OrderStatus } from "@tasty/api";
import { Badge } from "@/components/ui/badge";
import { Clock, ChevronRight } from "lucide-react";
import { cn, formatPrice } from "@/lib/utils";
import moment from "moment";

interface OrderListProps {
  orders: Order[];
  loading: boolean;
  onOrderClick: (orderId: string) => void;
}

const statusColors: Partial<Record<OrderStatus, string>> = {
  [OrderStatus.NEW]: "bg-blue-500",
  [OrderStatus.COOKING]: "bg-orange-500",
  [OrderStatus.READY_FOR_PICKUP]: "bg-green-500",
  [OrderStatus.ACCEPTED]: "bg-blue-400",
  [OrderStatus.PICKED_UP]: "bg-gray-500",
  [OrderStatus.COMPLETED]: "bg-gray-400",
  [OrderStatus.DECLINED_BY_RESTAURANT]: "bg-destructive",
};

export default function OrderList({
  orders,
  loading,
  onOrderClick,
}: OrderListProps) {
  if (loading && orders.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="h-14 w-full bg-muted/50 animate-pulse rounded-lg"
          />
        ))}
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="py-20 text-center border-2 border-dashed rounded-3xl bg-muted/5">
        <p className="text-sm text-muted-foreground font-medium">
          Keine Bestellungen vorhanden.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-1">
      {orders.map((order) => (
        <div
          key={order.id}
          onClick={() => onOrderClick(order.id)}
          className="group flex items-center justify-between p-2.5 bg-background border border-muted-foreground/5 rounded-lg hover:shadow-md hover:border-primary/20 transition-all cursor-pointer relative overflow-hidden"
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div
              className={cn(
                "w-1 h-6 rounded-full",
                statusColors[order.status] || "bg-muted"
              )}
            />

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-0">
                <span className="font-black text-xs tabular-nums tracking-tighter">
                  #{order.id.slice(-4).toUpperCase()}
                </span>
                <span className="font-bold text-xs truncate">
                  Bestellung {order.id.slice(0, 4)}
                </span>
              </div>
              <div className="flex items-center gap-3 text-[10px] text-muted-foreground font-medium leading-none mt-0.5">
                <div className="flex items-center gap-1">
                  <Clock className="h-2.5 w-2.5" />
                  {moment(order.createdAt).format("HH:mm")}
                </div>
                <span>{order.OrderDishes?.items?.length || 0} Artikel</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 ml-4">
            <div className="text-right">
              <div className="text-xs font-black tabular-nums">
                {formatPrice(order.total || 0)}
              </div>
              <Badge
                variant="outline"
                className="h-3.5 text-[7px] uppercase tracking-tighter px-1 border-muted-foreground/20 leading-none font-bold"
              >
                {order.status}
              </Badge>
            </div>
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
          </div>
        </div>
      ))}
    </div>
  );
}
